<template>
  <div>
    <div
      class="map-container inset-x-0 top-0 overflow-hidden"
      :class="{ 'pointer-events-none': !show && ['sm', 'md'].includes($screen.name), absolute: !isStickyOnScroll, 'z-30': ['sm', 'md'].includes($screen.name) }"
      :style="{ top: mapTop && `${mapTop}px`, bottom: `${mapBottom}px` }"
    >
      <div v-if="show || ['lg', 'xl'].includes($screen.name)" class="absolute inset-0 bg-white lg:block">
        <div ref="map" class="h-full" />
        <button
          v-if="showSearchArea && withSearchArea"
          class="absolute left-12 top-3 h-8 rounded bg-white px-4 py-1 text-sm font-medium text-gray-1 hover:bg-gray-6"
          :disabled="loadingVenues"
          @click="searchArea"
        >
          Search this area
        </button>
        <a
          v-if="show && !hideClose"
          href="#"
          class="absolute right-4 top-3 flex size-8 rounded bg-white text-center text-white lg:hidden"
          @click.prevent="$emit('update:show', false)"
        >
          <svg
            class="m-auto block"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M8 9.46788L1.46794 16L0 14.532L6.53206 7.99992L0.000156405 1.46796L1.4681 0L8 6.53196L14.5319 0L15.9998 1.46796L9.46794 7.99992L16 14.532L14.5321 16L8 9.46788Z" fill="#333333" />
          </svg>
        </a>
      </div>
      <div v-if="(loading || loadingVenues) && (show || $screen.no > 2)" class="absolute inset-0 flex items-center justify-center bg-white/75">
        <SpinnerSm />
      </div>
      <a
        v-if="!show"
        href="#"
        class="letter-sp15 pointer-events-auto fixed inset-x-0 bottom-0 block bg-white pb-8 pt-4 text-center text-sm font-medium uppercase text-blue shadow-dropdown-footer lg:hidden"
        @click.prevent="showMap"
      >Map view</a>
    </div>
    <div v-if="show && ['sm', 'md'].includes($screen.name)" class="fixed inset-0 top-auto bg-white lg:hidden" :style="{ height: `${mapBottom}px` }">
      <VenueMobile v-if="clickedVenue" :venue="clickedVenue" @close="clickedVenue = null" />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce'

import { useRuntimeConfig } from 'nuxt/app'
import { removeClass, toggleClass } from '~/utils/class'
import VenueMobile from '~/components/venues/VenueMobile'
import SpinnerSm from '~/components/shared/SpinnerSm'

const markerActive = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="250 250 360 360"><defs><clipPath id="clip-path"><rect class="cls-1" x="300.02" y="255.11" width="267.27" height="354.88"/></clipPath></defs><g id="Layer_3" data-name="Layer 3"><g class="cls-2"><path class="cls-3" d="M433.66,269.56c-65.7,0-119.19,53.65-119.19,119.56,0,83.45,74.67,166.26,111.51,203.22a10.82,10.82,0,0,0,15.33,0l0,0c36.85-37,111.52-119.77,111.52-203.22,0-65.91-53.49-119.56-119.19-119.56"/><path class="cls-4" d="M433.66,610h0a25.12,25.12,0,0,1-17.89-7.46C363,549.58,300,469.57,300,389.12c0-73.9,60-134,133.64-134s133.64,60.11,133.64,134c0,80.31-62.72,160.19-115.46,213.15l-.31.32A25.13,25.13,0,0,1,433.66,610Zm0-326c-57.76,0-104.74,47.15-104.74,105.11,0,38.57,18.26,102.81,104.74,190.44C520.14,491.93,538.4,427.7,538.4,389.12,538.4,331.16,491.41,284,433.66,284Z"/></g></g></svg>'
let mapboxgl

export default {
  components: { SpinnerSm, VenueMobile },

  props: {
    destination: { type: Object, default: null },
    venues: { type: Array, required: true },
    loadingVenues: Boolean,
    venueClicked: { type: Object, default: null },
    hoveringVenueId: { type: Number, default: null },
    show: Boolean,
    hideClose: Boolean,
    withSearchArea: { type: Boolean, default: true },
    isStickyOnScroll: { type: Boolean, default: true },
  },

  emits: ['markerClicked', 'update:show', 'searchArea'],

  data: () => ({
    mapStyle: 'mapbox://styles/tripexpertweb/ckcdpjifn06ru1impw321xsqi',
    map: null,
    markers: [],
    preventShowSearchArea: false,
    showSearchArea: false,
    firstLoad: true,
    clickedVenue: null,
    topBarHeight: 66,
    headerHeight: 127 + 40,
    footerHeight: 357 + 160, // footer + footer margin,
    mapTop: 237,
    mapBottom: 0,
    loading: false,
  }),

  computed: {
    bounds () {
      const venues = this.venues.filter(v => v.longitude && v.latitude)

      return [
        [
          Math.min(...venues.map(v => v.longitude)),
          Math.min(...venues.map(v => v.latitude)),
        ],
        [
          Math.max(...venues.map(v => v.longitude)),
          Math.max(...venues.map(v => v.latitude)),
        ],
      ]
    },

    mapCenter () {
      if (this.destination) {
        return [this.destination.longitude, this.destination.latitude]
      }
      return [0, 0]
    },
  },

  watch: {
    show (val) {
      if (val) {
        this.initMap()
      } else {
        this.clickedVenue = null
      }
    },

    venues () {
      this.handleScroll()
      this.addMarkers()
      this.fitBounds()
      this.showSearchArea = false
      this.firstLoad = false
      setTimeout(() => {
        this.handleScroll()
        setTimeout(() => this.fitBounds(), 200)
      }, 200)
    },

    venueClicked (venue) {
      if (! venue) { return }
      this.map.flyTo({
        center: [venue.longitude, venue.latitude],
        zoom: 14,
      })
    },

    '$screen.name': function (val) {
      if (val === 'sm' || val === 'md') {
        this.mapTop = null
      } else {
        this.initMap()
        this.handleScroll()
      }
    },

    hoveringVenueId (id) {
      if (! process.client) { return }
      const el = window.document.getElementById(`marker-${id}`)
      this.toggleMarkerActive(el)
    },

    clickedVenue (val) {
      if (! ['sm', 'md'].includes(this.$screen.name)) { return }
      if (val) {
        this.mapBottom = this.$screen.name === 'sm' ? 300 : 215
      } else {
        this.mapBottom = 0
      }
      this.resizeMap()
    },
  },

  created () {
    if (! process.client) { return }

    window.addEventListener('scroll', this.handleScroll)
    if (['sm', 'md'].includes(this.$screen.name)) {
      this.mapTop = 0
    }
  },

  unmounted () {
    window.removeEventListener('scroll', this.handleScroll)
  },

  mounted () {
    if (! process.client) { return }
    if (this.show || ['lg', 'xl'].includes(this.$screen.name)) {
      this.initMap()
    }
  },

  methods: {
    async initMap () {
      this.loading = true
      mapboxgl = (await import('mapbox-gl')).default
      mapboxgl.accessToken = useRuntimeConfig().public.mapboxApiToken
      this.map = new mapboxgl.Map({
        container: this.$refs.map,
        style: 'mapbox://styles/tripexpertweb/ckcdpjifn06ru1impw321xsqi',
        center: this.mapCenter,
        zoom: 11.15,
      })
      this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-left')
      if (this.venues.length) {
        this.addMarkers()
        this.fitBounds()
      }
      this.map.on('dragend', () => (this.showSearchArea = true))
      this.map.on('zoomend', () => {
        if (! this.preventShowSearchArea) { this.showSearchArea = true }
      })
      this.loading = false
    },

    addMarkers () {
      if (! process.client) { return }
      if (! mapboxgl) { return }
      if (this.markers.length) {
        this.markers.forEach(marker => marker.remove())
        this.markers = []
      }

      this.venues.filter(v => v.longitude && v.latitude).forEach((venue, index) => {
        // create DOM element for the marker
        const el = document.createElement('div')
        el.innerHTML = `<div class="marker-inner">${markerActive}<div class="maker-score">${Math.floor(venue.tripexpert_score / 10)}</div></div>`
        el.id = `marker-${venue.id}`
        el.className = 'marker'
        el.addEventListener('click', () => this.toggleMarkerActive(el))

        const marker = new mapboxgl.Marker(el) // new mapboxgl.Marker(el, {offset:[-25, -25]})
          .setLngLat([venue.longitude, venue.latitude])
          .setPopup(new mapboxgl.Popup({
            offset: {
              'top': [0, 10],
              'bottom': [0, -60],
              'left': [30, -23],
              'right': [-30, -23],
              'top-left': [25, 0],
              'top-right': [-25, 0],
              'bottom-left': [25, -50],
              'bottom-right': [-25, -50],
            },
            closeButton: false,
            closeOnMove: false,
            maxWidth: '300px',
          })
            .setHTML(`
              <a href="/${venue.path}" class="flex items-center focus-visible:outline-none">
                <div>
                  <h3 class="font-whitney-condensed break-words">${venue.name}</h3>
                </div>
                <div class="chevron-right">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.29936 16L-5.6797e-08 14.7006L6.70063 8L-6.42585e-07 1.29937L1.29936 -5.6797e-08L9.29936 8L1.29936 16Z" fill="white"/>
                  </svg>
                </div>
              </a>
           `))
          .addTo(this.map)

        marker.getElement().addEventListener('click', () => {
          this.markerClick(index)
        })

        this.markers.push(marker)
      })
    },

    toggleMarkerActive (el) {
      this.markers.forEach((m) => {
        removeClass(m.getElement(), 'active')
      })
      if (el) { toggleClass(el, 'active') }
    },

    fitBounds (duration = 1000) {
      if (! this.map) { return }
      if (! this.venues.length) { return }
      this.preventShowSearchArea = true
      if (this.venues.length === 1) {
        this.map.flyTo({ center: [this.venues[0].longitude, this.venues[0].latitude], zoom: 14, duration: this.firstLoad ? 0 : duration })
      } else {
        this.map.fitBounds(this.bounds, { padding: 30, duration: this.firstLoad ? 0 : duration })
      }
      this.map.once('moveend', () => (this.preventShowSearchArea = false))
    },

    markerClick (index) {
      this.clickedVenue = this.venues[index]
      this.$emit('markerClicked', index)
    },

    searchArea () {
      const bounds = this.map.getBounds()
      this.$emit('searchArea', {
        top_left: `${bounds._ne.lat},${bounds._sw.lng}`,
        bottom_right: `${bounds._sw.lat},${bounds._ne.lng}`,
      })
    },

    showMap () {
      this.$emit('update:show', true)
      setTimeout(() => {
        this.map.resize()
        this.fitBounds(0)
      }, 50)
    },

    handleScroll () {
      if (! process.client) { return }
      if (['sm', 'md'].includes(this.$screen.name)) {
        this.mapTop = 0
        return
      }
      const top = this.topBarHeight + Math.max(0, this.headerHeight - window.scrollY)
      const bottom = Math.max(0, this.footerHeight - (window.document.body.clientHeight - (window.scrollY + window.innerHeight)))
      const shouldResize = this.mapTop !== top || this.mapBottom !== bottom
      this.mapTop = top
      this.mapBottom = bottom
      if (shouldResize) { this.resizeMap() }
    },

    resizeMap: debounce(function () {
      if (! this.map) { return }
      this.map.resize()
    }, 10),
  },
}
</script>

<style lang="scss" scoped>
  $headerHeight: 237px;

  .map-container:not(.absolute) {
    position: fixed;

    @media (min-width: 913px) {
      left: 40rem;
      @apply ml-5;
    }
  }
  .map-container.absolute {
    top: 0 !important;
    bottom: auto !important;
    height: 100% !important;
  }

  :deep() {
    .mapboxgl-popup-content {
      @apply p-4 bg-slate text-white rounded-lg;
      h3 {
        @apply text-lg uppercase font-medium;
        letter-spacing: 0.1em;
      }
      p {
        @apply text-sm;
      }
      .chevron-right {
        @apply block pl-6 pr-1;
      }

      @media (max-width: 912px) {
        @apply hidden;
      }
    }

    .mapboxgl-popup-tip {
      @apply hidden;
    }

    .marker {
      @apply w-12 h-12 font-medium text-body text-center cursor-pointer;
      font-size: 22px; margin-top: -24px; line-height: 30px;

      &.active {
        @apply z-10;

        .cls-3 { fill: #d9f505; }
        .cls-4 { fill: #000000; }
        .maker-score {
          @apply text-black;
        }
      }

      .cls-1 { fill: none; }
      .cls-2 { clip-path: url(#clip-path); }

      .cls-3 { fill: #daf602; }
      .cls-4 { fill: #84857b; }
    }
    .marker-inner {
      position: relative;
    }
    .maker-score {
      @apply absolute inset-0 pt-1 leading-normal;
    }

    .mapboxgl-ctrl-top-left .mapboxgl-ctrl {
      @apply mt-3;
    }
  }
</style>
